// src/Services/checkoutService.ts
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage, app } from '../Firebase/Config';

// ── Initialise Functions SDK ──────────────────────────────────────────────────
const functions = getFunctions(app, 'asia-south1');
// To use the emulator locally, uncomment:
// import { connectFunctionsEmulator } from 'firebase/functions';
// connectFunctionsEmulator(functions, 'localhost', 5001);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

export interface OrderFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerWhatsApp: string;
  items: { productId: string; variantId?: string; quantity: string }[];
  subtotal: string;
  taxAmount: string;
  shippingCost: string;
  discountAmount: string;
  status: 'pending';
  paymentStatus: 'pending';
  paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
  shippingAddress: ShippingAddress;
  billingAddress: ShippingAddress;
  notes: string;
  trackingNumber: string;
  estimatedDelivery: string;
  allocatedWarehouseId?: string;
}

export interface PayhereParams {
  sandbox: boolean;
  merchant_id: string;
  return_url: string;
  cancel_url: string;
  notify_url: string;
  order_id: string;
  items: string;
  currency: string;
  amount: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  hash: string;
}

export interface InitiatePaymentResult {
  success: boolean;
  /**
   * The ID of the pending order document in `pendingOrders`.
   * This becomes the real order ID in `orders` once PayHere confirms
   * a successful payment via the payhereNotify webhook.
   * The frontend uses this same ID for the order-confirmation page URL.
   */
  pendingOrderId?: string;
  orderNumber?: string;
  payhereParams?: PayhereParams;
  message?: string;
}

// ── Callable function references ──────────────────────────────────────────────
// NOTE: initiatePayment is intentionally NOT here — it is now an onRequest
// function and is called via fetch() inside CheckoutService.initiatePayment().

const createOrderFn = httpsCallable<
  Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal'>,
  { success: boolean; orderId?: string; orderNumber?: string; message?: string }
>(functions, 'createOrder');

const savePaymentProofFn = httpsCallable<
  { orderId: string; url: string; type: 'image' | 'pdf'; fileName: string },
  { success: boolean }
>(functions, 'savePaymentProof');

const deletePaymentProofFn = httpsCallable<
  { orderId: string; downloadUrl: string },
  { success: boolean }
>(functions, 'deletePaymentProof');

const getOrderByIdFn = httpsCallable<
  { orderId: string },
  { success: boolean; order: Record<string, unknown> }
>(functions, 'getOrderById');

// ─────────────────────────────────────────────────────────────────────────────
// CheckoutService
// ─────────────────────────────────────────────────────────────────────────────

export class CheckoutService {

  // ── initiatePayment ───────────────────────────────────────────────────────
  /**
   * Calls the `initiatePayment` Cloud Function (onRequest) which:
   *  1. Validates products & prices server-side
   *  2. Creates a PENDING order in `pendingOrders` (NOT `orders`) in Firestore
   *  3. Returns a hash + all params needed to redirect to PayHere
   *
   * The real order in `orders` is ONLY created by the `payhereNotify` webhook
   * when PayHere confirms a successful payment (status_code = '2').
   * Failed or cancelled payments cause the pending doc to be deleted with
   * no order ever written to `orders`.
   *
   * Use this for credit_card / paypal flows (PayHere gateway).
   *
   * NOTE: This function is an onRequest (not onCall) endpoint. It is called
   * via fetch() with Content-Type: application/json, NOT via Firebase
   * httpsCallable. CORS is handled server-side in the Cloud Function.
   */
  static async initiatePayment(
    formData: OrderFormData
  ): Promise<InitiatePaymentResult> {
    try {
      const {
        status: _s,
        paymentStatus: _ps,
        subtotal: _sub,
        trackingNumber: _tn,
        estimatedDelivery: _ed,
        allocatedWarehouseId: _aw,
        ...payload
      } = formData;

      const response = await fetch(
        'https://asia-south1-kryptonian-ecom.cloudfunctions.net/initiatePayment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      const data: InitiatePaymentResult = await response.json();

      if (!response.ok) {
        // The onRequest function returns { success: false, message: '...' } on errors
        return {
          success: false,
          message: data.message || `Request failed with status ${response.status}`,
        };
      }

      return data;
    } catch (error: unknown) {
      console.error('initiatePayment Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to initiate payment. Please try again.' };
    }
  }

  // ── redirectToPayhere ─────────────────────────────────────────────────────
  /**
   * Builds a hidden HTML form with PayHere params and auto-submits it,
   * causing the browser to POST to PayHere's checkout page.
   *
   * PayHere requires a form POST (not a GET redirect) to pass params securely.
   */
  static redirectToPayhere(params: PayhereParams): void {
    const payhereBaseUrl = params.sandbox
      ? 'https://sandbox.payhere.lk/pay/checkout'
      : 'https://www.payhere.lk/pay/checkout';

    // Remove 'sandbox' from params — it's not a PayHere field
    const { sandbox: _sandbox, ...payhereFields } = params;

    // Build a hidden form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payhereBaseUrl;
    form.style.display = 'none';

    Object.entries(payhereFields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  // ── createOrder ───────────────────────────────────────────────────────────
  /**
   * Calls the `createOrder` Cloud Function directly.
   * Use ONLY for non-PayHere payment methods:
   *   - bank_transfer (proof uploaded separately)
   *   - cash_on_delivery
   */
  static async createOrder(
    formData: OrderFormData
  ): Promise<{ success: boolean; orderId?: string; orderNumber?: string; message?: string }> {
    try {
      const { status: _s, paymentStatus: _ps, subtotal: _sub, ...payload } = formData;
      const result = await createOrderFn(payload);
      return result.data;
    } catch (error: unknown) {
      console.error('createOrder Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to create order. Please try again.' };
    }
  }

  // ── uploadPaymentProof ────────────────────────────────────────────────────
  /**
   * Uploads a payment proof file directly to Firebase Storage from the client.
   * Call `savePaymentProofToOrder` afterwards to persist metadata.
   *
   * Path: order_payment_proofs/{orderId}/{timestamp}_{sanitisedFileName}
   */
  static async uploadPaymentProof(
    file: File,
    orderId: string,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
    const timestamp = Date.now();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
    const storageRef = ref(storage, storagePath);

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            onProgress(percent);
          }
        },
        (error) => {
          console.error('Payment proof upload error:', error);
          reject(new Error(`Upload failed: ${error.message}`));
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const type: 'image' | 'pdf' =
              file.type === 'application/pdf' ? 'pdf' : 'image';
            resolve({ url, type, fileName: file.name });
          } catch (err) {
            reject(new Error('Failed to get download URL after upload'));
          }
        }
      );
    });
  }

  // ── deletePaymentProof ────────────────────────────────────────────────────
  /**
   * Calls the `deletePaymentProof` Cloud Function, which removes the file
   * from Storage and clears proof metadata on the order document.
   * Falls back to client-side Storage deletion if the function call fails.
   */
  static async deletePaymentProof(downloadUrl: string, orderId?: string): Promise<void> {
    if (orderId) {
      try {
        await deletePaymentProofFn({ orderId, downloadUrl });
        return;
      } catch (fnError) {
        console.warn('deletePaymentProof Cloud Function failed, falling back to client delete:', fnError);
      }
    }

    try {
      const storageRef = ref(storage, downloadUrl);
      await deleteObject(storageRef);
    } catch (error: unknown) {
      const code = (error as Record<string, unknown>)?.code;
      if (code === 'storage/object-not-found') return;
      console.error('Error deleting payment proof:', error);
      throw new Error(`Failed to delete payment proof`);
    }
  }

  // ── savePaymentProofToOrder ───────────────────────────────────────────────
  /**
   * Calls the `savePaymentProof` Cloud Function to persist proof metadata
   * onto the order document in Firestore.
   * Call this after a successful `uploadPaymentProof`.
   */
  static async savePaymentProofToOrder(
    orderId: string,
    proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
  ): Promise<void> {
    if (!proofData) {
      console.warn('savePaymentProofToOrder called with null proofData — skipping');
      return;
    }

    try {
      await savePaymentProofFn({
        orderId,
        url: proofData.url,
        type: proofData.type,
        fileName: proofData.fileName,
      });
    } catch (error) {
      console.error('savePaymentProofToOrder Cloud Function error:', error);
      throw error;
    }
  }

  // ── getOrderById ──────────────────────────────────────────────────────────
  /**
   * Fetches a single confirmed order by its Firestore document ID.
   *
   * For PayHere orders: this will return null / throw until the
   * payhereNotify webhook fires and creates the order in `orders`.
   * The OrderConfirmation page should poll this until it resolves.
   *
   * Error message codes returned in the `not-found` HttpsError:
   *   'ORDER_PENDING_PAYMENT' — order still in pendingOrders, keep polling
   *   'ORDER_NOT_FOUND'       — payment failed/cancelled, or bad ID
   */
  static async getOrderById(
    orderId: string
  ): Promise<{ success: boolean; order?: Record<string, unknown>; message?: string }> {
    try {
      const result = await getOrderByIdFn({ orderId });
      return result.data;
    } catch (error: unknown) {
      console.error('getOrderById Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to fetch order.' };
    }
  }
}

// // src/Services/checkoutService.ts
// import {
//   ref,
//   uploadBytesResumable,
//   getDownloadURL,
//   deleteObject,
// } from 'firebase/storage';
// import { getFunctions, httpsCallable } from 'firebase/functions';
// import { storage, app } from '../Firebase/Config';

// // ── Initialise Functions SDK ──────────────────────────────────────────────────
// const functions = getFunctions(app, 'asia-south1');
// // To use the emulator locally, uncomment:
// // import { connectFunctionsEmulator } from 'firebase/functions';
// // connectFunctionsEmulator(functions, 'localhost', 5001);

// // ── Types ─────────────────────────────────────────────────────────────────────

// export interface ShippingAddress {
//   fullName: string;
//   addressLine1: string;
//   addressLine2: string;
//   city: string;
//   state: string;
//   postalCode: string;
//   country: string;
//   phoneNumber: string;
// }

// export interface OrderFormData {
//   customerName: string;
//   customerEmail: string;
//   customerPhone: string;
//   customerWhatsApp: string;
//   items: { productId: string; variantId?: string; quantity: string }[];
//   subtotal: string;
//   taxAmount: string;
//   shippingCost: string;
//   discountAmount: string;
//   status: 'pending';
//   paymentStatus: 'pending';
//   paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
//   shippingAddress: ShippingAddress;
//   billingAddress: ShippingAddress;
//   notes: string;
//   trackingNumber: string;
//   estimatedDelivery: string;
//   allocatedWarehouseId?: string;
// }

// export interface PayhereParams {
//   sandbox: boolean;
//   merchant_id: string;
//   return_url: string;
//   cancel_url: string;
//   notify_url: string;
//   order_id: string;
//   items: string;
//   currency: string;
//   amount: string;
//   first_name: string;
//   last_name: string;
//   email: string;
//   phone: string;
//   address: string;
//   city: string;
//   country: string;
//   hash: string;
// }

// export interface InitiatePaymentResult {
//   success: boolean;
//   /**
//    * The ID of the pending order document in `pendingOrders`.
//    * This becomes the real order ID in `orders` once PayHere confirms
//    * a successful payment via the payhereNotify webhook.
//    * The frontend uses this same ID for the order-confirmation page URL.
//    */
//   pendingOrderId?: string;
//   orderNumber?: string;
//   payhereParams?: PayhereParams;
//   message?: string;
// }

// // ── Callable function references ──────────────────────────────────────────────

// const initiatePaymentFn = httpsCallable<
//   Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal' | 'trackingNumber' | 'estimatedDelivery' | 'allocatedWarehouseId'>,
//   InitiatePaymentResult
// >(functions, 'initiatePayment');

// const createOrderFn = httpsCallable<
//   Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal'>,
//   { success: boolean; orderId?: string; orderNumber?: string; message?: string }
// >(functions, 'createOrder');

// const savePaymentProofFn = httpsCallable<
//   { orderId: string; url: string; type: 'image' | 'pdf'; fileName: string },
//   { success: boolean }
// >(functions, 'savePaymentProof');

// const deletePaymentProofFn = httpsCallable<
//   { orderId: string; downloadUrl: string },
//   { success: boolean }
// >(functions, 'deletePaymentProof');

// const getOrderByIdFn = httpsCallable<
//   { orderId: string },
//   { success: boolean; order: Record<string, unknown> }
// >(functions, 'getOrderById');

// // ─────────────────────────────────────────────────────────────────────────────
// // CheckoutService
// // ─────────────────────────────────────────────────────────────────────────────

// export class CheckoutService {

//   // ── initiatePayment ───────────────────────────────────────────────────────
//   /**
//    * Calls the `initiatePayment` Cloud Function which:
//    *  1. Validates products & prices server-side
//    *  2. Creates a PENDING order in `pendingOrders` (NOT `orders`) in Firestore
//    *  3. Returns a hash + all params needed to redirect to PayHere
//    *
//    * The real order in `orders` is ONLY created by the `payhereNotify` webhook
//    * when PayHere confirms a successful payment (status_code = '2').
//    * Failed or cancelled payments cause the pending doc to be deleted with
//    * no order ever written to `orders`.
//    *
//    * Use this for credit_card / paypal flows (PayHere gateway).
//    */
//   static async initiatePayment(
//     formData: OrderFormData
//   ): Promise<InitiatePaymentResult> {
//     try {
//       const {
//         status: _s,
//         paymentStatus: _ps,
//         subtotal: _sub,
//         trackingNumber: _tn,
//         estimatedDelivery: _ed,
//         allocatedWarehouseId: _aw,
//         ...payload
//       } = formData;

//       const result = await initiatePaymentFn(payload);
//       return result.data;
//     } catch (error: unknown) {
//       console.error('initiatePayment Cloud Function error:', error);
//       if (
//         error &&
//         typeof error === 'object' &&
//         'message' in error &&
//         typeof (error as { message: unknown }).message === 'string'
//       ) {
//         return { success: false, message: (error as { message: string }).message };
//       }
//       return { success: false, message: 'Failed to initiate payment. Please try again.' };
//     }
//   }

//   // ── redirectToPayhere ─────────────────────────────────────────────────────
//   /**
//    * Builds a hidden HTML form with PayHere params and auto-submits it,
//    * causing the browser to POST to PayHere's checkout page.
//    *
//    * PayHere requires a form POST (not a GET redirect) to pass params securely.
//    */
//   static redirectToPayhere(params: PayhereParams): void {
//     const payhereBaseUrl = params.sandbox
//       ? 'https://sandbox.payhere.lk/pay/checkout'
//       : 'https://www.payhere.lk/pay/checkout';

//     // Remove 'sandbox' from params — it's not a PayHere field
//     const { sandbox: _sandbox, ...payhereFields } = params;

//     // Build a hidden form and submit it
//     const form = document.createElement('form');
//     form.method = 'POST';
//     form.action = payhereBaseUrl;
//     form.style.display = 'none';

//     Object.entries(payhereFields).forEach(([key, value]) => {
//       const input = document.createElement('input');
//       input.type = 'hidden';
//       input.name = key;
//       input.value = String(value);
//       form.appendChild(input);
//     });

//     document.body.appendChild(form);
//     form.submit();
//   }

//   // ── createOrder ───────────────────────────────────────────────────────────
//   /**
//    * Calls the `createOrder` Cloud Function directly.
//    * Use ONLY for non-PayHere payment methods:
//    *   - bank_transfer (proof uploaded separately)
//    *   - cash_on_delivery
//    */
//   static async createOrder(
//     formData: OrderFormData
//   ): Promise<{ success: boolean; orderId?: string; orderNumber?: string; message?: string }> {
//     try {
//       const { status: _s, paymentStatus: _ps, subtotal: _sub, ...payload } = formData;
//       const result = await createOrderFn(payload);
//       return result.data;
//     } catch (error: unknown) {
//       console.error('createOrder Cloud Function error:', error);
//       if (
//         error &&
//         typeof error === 'object' &&
//         'message' in error &&
//         typeof (error as { message: unknown }).message === 'string'
//       ) {
//         return { success: false, message: (error as { message: string }).message };
//       }
//       return { success: false, message: 'Failed to create order. Please try again.' };
//     }
//   }

//   // ── uploadPaymentProof ────────────────────────────────────────────────────
//   /**
//    * Uploads a payment proof file directly to Firebase Storage from the client.
//    * Call `savePaymentProofToOrder` afterwards to persist metadata.
//    *
//    * Path: order_payment_proofs/{orderId}/{timestamp}_{sanitisedFileName}
//    */
//   static async uploadPaymentProof(
//     file: File,
//     orderId: string,
//     onProgress?: (percent: number) => void
//   ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
//     const timestamp = Date.now();
//     const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
//     const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
//     const storageRef = ref(storage, storagePath);

//     return new Promise((resolve, reject) => {
//       const uploadTask = uploadBytesResumable(storageRef, file, {
//         contentType: file.type,
//       });

//       uploadTask.on(
//         'state_changed',
//         (snapshot) => {
//           if (onProgress) {
//             const percent = Math.round(
//               (snapshot.bytesTransferred / snapshot.totalBytes) * 100
//             );
//             onProgress(percent);
//           }
//         },
//         (error) => {
//           console.error('Payment proof upload error:', error);
//           reject(new Error(`Upload failed: ${error.message}`));
//         },
//         async () => {
//           try {
//             const url = await getDownloadURL(uploadTask.snapshot.ref);
//             const type: 'image' | 'pdf' =
//               file.type === 'application/pdf' ? 'pdf' : 'image';
//             resolve({ url, type, fileName: file.name });
//           } catch (err) {
//             reject(new Error('Failed to get download URL after upload'));
//           }
//         }
//       );
//     });
//   }

//   // ── deletePaymentProof ────────────────────────────────────────────────────
//   /**
//    * Calls the `deletePaymentProof` Cloud Function, which removes the file
//    * from Storage and clears proof metadata on the order document.
//    * Falls back to client-side Storage deletion if the function call fails.
//    */
//   static async deletePaymentProof(downloadUrl: string, orderId?: string): Promise<void> {
//     if (orderId) {
//       try {
//         await deletePaymentProofFn({ orderId, downloadUrl });
//         return;
//       } catch (fnError) {
//         console.warn('deletePaymentProof Cloud Function failed, falling back to client delete:', fnError);
//       }
//     }

//     try {
//       const storageRef = ref(storage, downloadUrl);
//       await deleteObject(storageRef);
//     } catch (error: unknown) {
//       const code = (error as Record<string, unknown>)?.code;
//       if (code === 'storage/object-not-found') return;
//       console.error('Error deleting payment proof:', error);
//       throw new Error(`Failed to delete payment proof`);
//     }
//   }

//   // ── savePaymentProofToOrder ───────────────────────────────────────────────
//   /**
//    * Calls the `savePaymentProof` Cloud Function to persist proof metadata
//    * onto the order document in Firestore.
//    * Call this after a successful `uploadPaymentProof`.
//    */
//   static async savePaymentProofToOrder(
//     orderId: string,
//     proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
//   ): Promise<void> {
//     if (!proofData) {
//       console.warn('savePaymentProofToOrder called with null proofData — skipping');
//       return;
//     }

//     try {
//       await savePaymentProofFn({
//         orderId,
//         url: proofData.url,
//         type: proofData.type,
//         fileName: proofData.fileName,
//       });
//     } catch (error) {
//       console.error('savePaymentProofToOrder Cloud Function error:', error);
//       throw error;
//     }
//   }

//   // ── getOrderById ──────────────────────────────────────────────────────────
//   /**
//    * Fetches a single confirmed order by its Firestore document ID.
//    *
//    * For PayHere orders: this will return null / throw until the
//    * payhereNotify webhook fires and creates the order in `orders`.
//    * The OrderConfirmation page should poll this until it resolves.
//    *
//    * Error message codes returned in the `not-found` HttpsError:
//    *   'ORDER_PENDING_PAYMENT' — order still in pendingOrders, keep polling
//    *   'ORDER_NOT_FOUND'       — payment failed/cancelled, or bad ID
//    */
//   static async getOrderById(
//     orderId: string
//   ): Promise<{ success: boolean; order?: Record<string, unknown>; message?: string }> {
//     try {
//       const result = await getOrderByIdFn({ orderId });
//       return result.data;
//     } catch (error: unknown) {
//       console.error('getOrderById Cloud Function error:', error);
//       if (
//         error &&
//         typeof error === 'object' &&
//         'message' in error &&
//         typeof (error as { message: unknown }).message === 'string'
//       ) {
//         return { success: false, message: (error as { message: string }).message };
//       }
//       return { success: false, message: 'Failed to fetch order.' };
//     }
//   }
// }
